import React, { useState, useEffect } from "react";
import { Box, IconButton, Divider, MenuItem, Select, FormControl, InputLabel,   Tooltip,
  styled,
  tooltipClasses } from "@mui/material";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { selectDataByIndicator, selectYearToDateData } from "../app/dataSelectors";
import LineGraphVisualization from "./LineGraphVisualization";
import StackedBarVisualization from "./StackedBarVisualization";
import "./TimePeriodVisualization.css";
import { CONFLICT_RESOLUTION_PROGRAM } from '../services/util';
import { useSelector } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


const getFirstAndLastDates = (data) => {
  if (!data.length) return { firstDate: "", lastDate: "" };
  const options = { year: 'numeric', month: 'long' };
  const sortedData = [...data].sort((a, b) => new Date(a.Month) - new Date(b.Month));
  const firstDate = new Date(sortedData[0].Month).toLocaleDateString(undefined, options);
  const lastDate = new Date(sortedData[sortedData.length - 1].Month).toLocaleDateString(undefined, options);

  return { firstDate, lastDate };
};

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

const TimePeriodVisualization = ({ data, indicators, stateName }) => {
  const timePeriods = ["Year-to-date", "All time", "Custom"];
  const [selectedTimePeriodIndex, setSelectedTimePeriodIndex] = useState(0);
  const [selectedIndicator, setSelectedIndicator] = useState(indicators[0]?.label || "");
  const aggregatedData = useSelector(selectDataByIndicator(selectedIndicator));
  const totalTimePeriod = getFirstAndLastDates(data);
  const [startDate, setStartDate] = useState(totalTimePeriod.firstDate);
  const [endDate, setEndDate] = useState(totalTimePeriod.lastDate);

  const selectedTimePeriod = timePeriods[selectedTimePeriodIndex];

  const filterDataByTimePeriod = (data, timePeriod) => {
    switch (timePeriod) {
      case "Year-to-date":
        return selectYearToDateData(data)();
      case "All time":
        return data;
      case "Custom":
        return data.filter(item => {
          const itemDate = new Date(item.Month);
          const start = new Date(startDate);
          const adjustedStart = new Date(start.setDate(start.getDate() - 1));
          const end = new Date(endDate);
          return itemDate >= adjustedStart && itemDate <= end;
        });
      default:
        return data;
    }
  };

  const timePeriodData = filterDataByTimePeriod(data, selectedTimePeriod);
  const { firstDate, lastDate } = getFirstAndLastDates(timePeriodData);

  const handlePrevTimePeriod = () => {
    if (selectedTimePeriodIndex > 0) {
      setSelectedTimePeriodIndex(selectedTimePeriodIndex - 1);
    }
  };

  const handleNextTimePeriod = () => {
    if (selectedTimePeriodIndex < timePeriods.length - 1) {
      setSelectedTimePeriodIndex(selectedTimePeriodIndex + 1);
    }
  };

  const handleIndicatorChange = (event) => {
    setSelectedIndicator(event.target.value);
  };

  const getFilteredData = () => {
    if (stateName === CONFLICT_RESOLUTION_PROGRAM.name) {
      return filterDataByTimePeriod(aggregatedData, timePeriods[selectedTimePeriodIndex]);
    }
    return timePeriodData;
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  useEffect(() => {
    setSelectedTimePeriodIndex(0);
  }, [data, indicators, stateName]);

  return (
    <Box className="visualization-container">
      <Box className="month-selector" style={{ marginTop: "20px" }}>
        <IconButton onClick={handlePrevTimePeriod} disabled={selectedTimePeriodIndex === 0}>
          <ArrowLeftIcon sx={{ fontSize: 40 }} />
        </IconButton>
        <IconButton onClick={handleNextTimePeriod} disabled={selectedTimePeriodIndex === timePeriods.length - 1}>
          <ArrowRightIcon sx={{ fontSize: 40 }}/>
        </IconButton>
        <p className="month-selected-text">{selectedTimePeriod}</p>
        <HtmlTooltip title={"Click on the arrows to select the time period you’re interested in."} placement="right-start">
          <InfoOutlinedIcon className="time-period-tooltip"/>
        </HtmlTooltip>
      </Box>
      {selectedTimePeriod === "Custom" && (
          <Box className="custom-period-selector">
            <FormControl className="date-selector">
              <InputLabel id="custom-start-date-label">Start Date</InputLabel>
              <Select
                labelId="custom-start-date-label"
                value={startDate}
                onChange={handleStartDateChange}
              >
                {data.map(item => (
                  <MenuItem key={item.Month} value={item.Month}>
                    {new Date(item.Month).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="date-selector">
              <InputLabel id="custom-end-date-label">End Date</InputLabel>
              <Select
                labelId="custom-end-date-label"
                value={endDate}
                onChange={handleEndDateChange}
              >
                {data.map(item => (
                  <MenuItem key={item.Month} value={item.Month}>
                    {new Date(item.Month).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      <Divider />
      <Box className="visualization-description">
        <p className="headline-title">Visualize the Data</p>
        {stateName === CONFLICT_RESOLUTION_PROGRAM.name ? 
        <p className="headline-body"> Take a closer look at the data to see how it differs across states and over time.</p> :
        <p className="headline-body">Create custom visualizations of one or more indicators to see how the data has changed over time.</p>
        }
      </Box>
      <Box className="visualization-container visualization-border">
        {stateName === CONFLICT_RESOLUTION_PROGRAM.name && (
          <Box className="indicator-selector" style={{ margin: "20px 0" }}>
            <FormControl fullWidth>
              <InputLabel id="indicator-select-label">Indicator</InputLabel>
              <Select
                labelId="indicator-select-label"
                value={selectedIndicator}
                label="Indicator"
                onChange={handleIndicatorChange}
              >
                {indicators.map((indicator, index) => (
                  <MenuItem key={index} value={indicator.label}>
                    {indicator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {stateName !== CONFLICT_RESOLUTION_PROGRAM.name ? 
          <>
            <p className="graph-title">Selected Indicators ({firstDate} - {lastDate})</p>
          </> : 
          <>
            <p className="graph-title">{selectedIndicator}, by State</p> 
            <p className="graph-title"> ({firstDate} - {lastDate})</p>
          </>
        }
        
        {stateName === CONFLICT_RESOLUTION_PROGRAM.name ? 
          <LineGraphVisualization
            data={getFilteredData()}
            indicators={selectedIndicator}
            crp={true}
          />
          :
          <LineGraphVisualization
            data={getFilteredData()}
            indicators={indicators}
          />
        }
      </Box>
      <Box className="visualization-container visualization-border">
        <p className="graph-title">Sum Total for Each Indicator, by Month ({firstDate} - {lastDate})</p>
        <StackedBarVisualization data={timePeriodData} indicators={indicators} stateName={stateName} />
      </Box>
    </Box>
  );
};

export default TimePeriodVisualization;
